/**
 * skillMatrixStore.ts
 * Zustand store with zundo temporal middleware for the Skill Matrix page.
 *
 * Holds the flat SkillMatrixCell[] that the grid renders. All writes go
 * through `applyAndPersist` which:
 *   1. Patches local state optimistically (via patchCellLevel).
 *   2. Fires batchUpsertSkillLevels against the server.
 *   3. Reverts and re-throws on API failure.
 *
 * Undo/redo:
 *   `safeUndo` / `safeRedo` wrap zundo's undo/redo with server-sync:
 *   they diff the cells before and after the temporal jump and send a
 *   batchUpsert for any cell whose Level changed. If the sync fails they
 *   push the jump back (redo after undo, undo after redo) and re-throw.
 *
 * Initial-load isolation:
 *   `loadCellsFromServer` pauses the temporal middleware, replaces cells,
 *   resumes, then clears history so the first Ctrl+Z after a page load
 *   does nothing (the user cannot undo back to "empty matrix").
 *
 * Pattern mirrors useReportTemplateStore in reportTemplates/ — see that
 * file for the precedent this store follows.
 */

import { create } from 'zustand';
import { temporal } from 'zundo';
import type { TemporalState } from 'zundo';
import {
  batchUpsertSkillLevels,
  type BatchCellUpdate,
  type SkillMatrixCell,
} from '../services/scheduleSkillMatrix';
import { patchCellLevel } from '../pages/schedule/views/skill-matrix/cellPatch';
import type { CellCoord } from '../pages/schedule/views/skill-matrix/selectionMath';

// ── Selection state ───────────────────────────────────────────────────────────

/**
 * Visual selection state for Excel-style range selection.
 *
 * anchor: where the selection started (Shift+click extends from here).
 * focus:  the current focused/active cell (focus ring lives here).
 *
 * The rendered rectangle is derived by resolveSelectedCoords(anchor, focus).
 * This state is excluded from undo/redo history via `partialize` below.
 */
export interface SelectionState {
  anchor: CellCoord | null;
  focus: CellCoord | null;
}

// ── State interface ───────────────────────────────────────────────────────────

interface SkillMatrixState {
  cells: SkillMatrixCell[];

  /** Visual range-selection state. NOT part of undo history. */
  selection: SelectionState;

  /**
   * Replace cells as a result of an initial server load. Bypasses undo
   * history: pauses temporal tracking, sets cells, resumes, then clears
   * history so Ctrl+Z cannot undo back to an empty grid.
   */
  loadCellsFromServer: (cells: SkillMatrixCell[]) => void;

  /**
   * Apply a heterogeneous set of cell updates optimistically (local state
   * first) and then persist them to the server. Reverts and throws on
   * API failure — callers should catch and show a toast.
   *
   * `fallbackCells` provides display-field data for cells that don't yet
   * exist in local state (so patchCellLevel can build a synthetic row).
   */
  applyAndPersist: (
    updates: BatchCellUpdate[],
    fallbackCells: SkillMatrixCell[],
  ) => Promise<void>;

  /** Sets both anchor and focus to `coord` (single-cell focus). */
  setFocus: (coord: CellCoord) => void;

  /** Keeps anchor, moves focus to `coord` (extends the selection rectangle). */
  extendSelection: (coord: CellCoord) => void;

  /** Clears both anchor and focus (no visual selection). */
  clearSelection: () => void;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useSkillMatrixStore = create<SkillMatrixState>()(
  temporal(
    (set, get) => ({
      cells: [],

      // Selection is pure visual state — initialised to "nothing selected".
      selection: { anchor: null, focus: null },

      loadCellsFromServer: (cells) => {
        // Pause so the set() call doesn't push a history entry.
        const temporal = useSkillMatrixStore.temporal.getState();
        temporal.pause();
        set({ cells });
        temporal.resume();
        // Clear any stale history from a previous load.
        temporal.clear();
      },

      applyAndPersist: async (updates, fallbackCells) => {
        const previous = get().cells;

        // Optimistic patch — apply all updates to local state immediately.
        const next = applyUpdates(previous, updates, fallbackCells);
        set({ cells: next });

        try {
          await batchUpsertSkillLevels(updates);
        } catch (e) {
          // Revert to the snapshot taken before the optimistic update.
          set({ cells: previous });
          throw e;
        }
      },

      setFocus: (coord) => {
        set({ selection: { anchor: coord, focus: coord } });
      },

      extendSelection: (coord) => {
        const current = get().selection;
        // If no anchor yet, treat this as a fresh focus.
        const anchor = current.anchor ?? coord;
        set({ selection: { anchor, focus: coord } });
      },

      clearSelection: () => {
        set({ selection: { anchor: null, focus: null } });
      },
    }),
    {
      // Only the cells array participates in undo/redo.
      // Selection is visual-only and should NOT be restored by Ctrl+Z.
      partialize: (s): Pick<SkillMatrixState, 'cells'> => ({ cells: s.cells }),
      // Keep the last 50 snapshots — enough for any realistic editing session.
      limit: 50,
    },
  ),
);

// ── Temporal store access ─────────────────────────────────────────────────────

/**
 * Returns the zundo temporal store — gives access to undo, redo, clear,
 * pause, resume, pastStates, futureStates.
 *
 * The cast to `TemporalState<Pick<SkillMatrixState, 'cells'>>` matches the
 * `partialize` option above.
 */
export function getTemporalStore(): TemporalState<Pick<SkillMatrixState, 'cells'>> {
  return (
    useSkillMatrixStore.temporal as unknown as {
      getState: () => TemporalState<Pick<SkillMatrixState, 'cells'>>;
    }
  ).getState();
}

/**
 * React hook that re-renders when undo/redo availability changes.
 *
 * The temporal store is a standard zustand store, so we can subscribe to it
 * with `useStore`. We select only the history lengths so the component only
 * re-renders when those specifically change (not on every temporal state write).
 */
export function useSkillMatrixHistoryCounts(): { pastSize: number; futureSize: number } {
  // Cast to the shape zustand's useStore expects.
  const temporalStore = useSkillMatrixStore.temporal as unknown as {
    getState: () => TemporalState<Pick<SkillMatrixState, 'cells'>>;
    subscribe: (listener: () => void) => () => void;
  };

  // Manual useSyncExternalStore-compatible subscription so this hook is
  // reactive without importing React inside the store module.
  // The parent component subscribes via the ref pattern in SkillMatrixPage;
  // this function simply reads the current snapshot.
  const state = temporalStore.getState();
  return {
    pastSize: state.pastStates.length,
    futureSize: state.futureStates.length,
  };
}

// ── Server-synced undo/redo ───────────────────────────────────────────────────

/**
 * Undo the last cell change(s) and synchronise the diff to the server.
 *
 * Steps:
 *   1. Snapshot cells BEFORE the undo.
 *   2. Call temporal.undo() — zundo restores previous cells locally.
 *   3. Snapshot cells AFTER the undo.
 *   4. Compute which cells changed (by composite key).
 *   5. Fire batchUpsertSkillLevels for those cells.
 *   6. On API failure: call temporal.redo() to restore the pre-undo state
 *      and re-throw so the caller can show a toast.
 */
export async function safeUndo(): Promise<void> {
  const before = useSkillMatrixStore.getState().cells;
  const temporal = getTemporalStore();

  if (temporal.pastStates.length === 0) return;

  temporal.undo();

  const after = useSkillMatrixStore.getState().cells;
  const diff = computeDiff(before, after);

  if (diff.length === 0) return;

  try {
    await batchUpsertSkillLevels(diff);
  } catch (e) {
    // Revert the temporal undo by redoing it.
    getTemporalStore().redo();
    throw e;
  }
}

/**
 * Redo the last undone cell change(s) and synchronise the diff to the server.
 *
 * Mirror of safeUndo: on API failure, undo() is called to restore the
 * pre-redo state.
 */
export async function safeRedo(): Promise<void> {
  const before = useSkillMatrixStore.getState().cells;
  const temporal = getTemporalStore();

  if (temporal.futureStates.length === 0) return;

  temporal.redo();

  const after = useSkillMatrixStore.getState().cells;
  const diff = computeDiff(before, after);

  if (diff.length === 0) return;

  try {
    await batchUpsertSkillLevels(diff);
  } catch (e) {
    // Revert the temporal redo by undoing it.
    getTemporalStore().undo();
    throw e;
  }
}

// ── Private helpers ───────────────────────────────────────────────────────────

/**
 * Apply a list of BatchCellUpdates to a cells array, returning a new array.
 *
 * For each update:
 *   - If the cell already exists: update its Level (or remove on null).
 *   - If the cell is new and Level !== null: try to find it in fallbackCells
 *     and append it; otherwise skip (next refetch will reconcile).
 *
 * Delegates each individual update to patchCellLevel so the same edge cases
 * (null → remove, missing fallback → skip) are handled consistently.
 */
function applyUpdates(
  cells: SkillMatrixCell[],
  updates: BatchCellUpdate[],
  fallbackCells: SkillMatrixCell[],
): SkillMatrixCell[] {
  // Build a lookup map from the fallback cells.
  const fallbackMap = new Map<string, SkillMatrixCell>();
  for (const fb of fallbackCells) {
    fallbackMap.set(`${fb.UserKey}::${fb.SkillKey}`, fb);
  }

  let result = cells;
  for (const update of updates) {
    const fallback = fallbackMap.get(`${update.UserKey}::${update.SkillKey}`);
    result = patchCellLevel(result, update.UserKey, update.SkillKey, update.Level, fallback);
  }
  return result;
}

/**
 * Compute the set of cell updates needed to transform `before` into `after`.
 * Returns BatchCellUpdate[] for cells whose Level differs by composite key.
 * A cell present in `before` but absent in `after` is treated as Level=null
 * (cleared); a cell absent in `before` but present in `after` gets its new
 * Level.
 */
function computeDiff(
  before: SkillMatrixCell[],
  after: SkillMatrixCell[],
): BatchCellUpdate[] {
  const beforeMap = new Map<string, number | null>();
  for (const c of before) {
    beforeMap.set(`${c.UserKey}::${c.SkillKey}`, c.Level ?? null);
  }

  const afterMap = new Map<string, SkillMatrixCell>();
  for (const c of after) {
    afterMap.set(`${c.UserKey}::${c.SkillKey}`, c);
  }

  const diff: BatchCellUpdate[] = [];

  // Cells that changed or were removed.
  for (const [key, beforeLevel] of beforeMap) {
    const afterCell = afterMap.get(key);
    const afterLevel = afterCell?.Level ?? null;
    if (afterLevel !== beforeLevel) {
      // Parse UserKey and SkillKey back from composite key.
      const sepIdx = key.indexOf('::');
      diff.push({
        UserKey: key.slice(0, sepIdx),
        SkillKey: key.slice(sepIdx + 2),
        Level: afterLevel,
      });
    }
  }

  // Cells that were added (not in before at all).
  for (const [key, afterCell] of afterMap) {
    if (!beforeMap.has(key) && afterCell.Level !== null) {
      const sepIdx = key.indexOf('::');
      diff.push({
        UserKey: key.slice(0, sepIdx),
        SkillKey: key.slice(sepIdx + 2),
        Level: afterCell.Level,
      });
    }
  }

  return diff;
}
